import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../config/api';

const QUICK_QUESTIONS = [
  'How do I prepare for dismissal?',
  'How do I add or update a student?',
  'How do I message families?',
];

export default function WebSupportChat({ token, schoolId }) {
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [handoff, setHandoff] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiFetch(`/webapp-support/conversation?school_id=${encodeURIComponent(schoolId)}`, {}, token)
      .then((response) => {
        if (!active) return;
        setConversationId(response.data?.conversation?.id ?? null);
        setMessages(response.data?.messages ?? []);
      })
      .catch((err) => { if (active) setError(err.message || 'Could not load AI Support.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [schoolId, token]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const send = useCallback(async (value) => {
    const question = String(value || '').trim();
    if (!question || sending) return;
    const pendingId = `pending-${Date.now()}`;
    setMessages((current) => [...current, { id: pendingId, role: 'user', body: question, pending: true }]);
    setDraft('');
    setSending(true);
    setError('');
    setHandoff(false);
    try {
      const response = await apiFetch('/webapp-support/messages', {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          conversation_id: conversationId,
          message: question,
          context: { platform: 'web', app_version: process.env.REACT_APP_VERSION || 'unknown', current_screen: window.location.pathname },
        }),
      }, token);
      setConversationId(response.data?.conversation?.id ?? conversationId);
      setMessages((current) => [
        ...current.filter((message) => message.id !== pendingId),
        response.data?.user_message,
        response.data?.assistant_message,
      ].filter(Boolean));
      setHandoff(Boolean(response.data?.handoff_recommended));
    } catch (err) {
      setMessages((current) => current.filter((message) => message.id !== pendingId));
      setDraft(question);
      setError(err.message || 'AI Support could not answer right now.');
    } finally {
      setSending(false);
    }
  }, [conversationId, schoolId, sending, token]);

  const feedback = async (messageId, value) => {
    setMessages((current) => current.map((message) => message.id === messageId ? { ...message, feedback: value } : message));
    try {
      await apiFetch(`/webapp-support/messages/${messageId}/feedback`, {
        method: 'POST', body: JSON.stringify({ school_id: schoolId, feedback: value }),
      }, token);
    } catch {
      setMessages((current) => current.map((message) => message.id === messageId ? { ...message, feedback: null } : message));
    }
  };

  return (
    <div className="sd-ai-chat">
      <div className="sd-ai-notice">AI answers can make mistakes. Your school procedures always take priority.</div>
      <div className="sd-ai-messages" ref={listRef}>
        {loading ? <div className="sd-ai-empty">Loading support history…</div> : null}
        {!loading && messages.length === 0 ? (
          <div className="sd-ai-empty">
            <strong>How can I help?</strong>
            <span>Ask about using the Car-Pool school dashboard.</span>
            <div className="sd-ai-quick-list">
              {QUICK_QUESTIONS.map((question) => (
                <button type="button" key={question} onClick={() => send(question)}>{question}</button>
              ))}
            </div>
          </div>
        ) : null}
        {messages.map((message) => (
          <div key={message.id} className={`sd-ai-message ${message.role === 'user' ? 'is-user' : 'is-assistant'}`}>
            <div className="sd-ai-bubble">{message.body}</div>
            {message.role === 'assistant' ? (
              <div className="sd-ai-feedback">
                Helpful?
                <button className={message.feedback === 'helpful' ? 'selected' : ''} type="button" onClick={() => feedback(message.id, 'helpful')}>Yes</button>
                <button className={message.feedback === 'not_helpful' ? 'selected' : ''} type="button" onClick={() => feedback(message.id, 'not_helpful')}>No</button>
              </div>
            ) : null}
          </div>
        ))}
        {sending ? <div className="sd-ai-typing">Car-Pool AI Support is thinking…</div> : null}
      </div>
      {handoff ? <div className="sd-ai-handoff">Please use one of the support options to contact our team for this request.</div> : null}
      {error ? <div className="sd-support-error">{error}</div> : null}
      <form className="sd-ai-compose" onSubmit={(event) => { event.preventDefault(); send(draft); }}>
        <textarea value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Ask about the school dashboard" maxLength={2000} disabled={sending} />
        <button type="submit" disabled={!draft.trim() || sending}>{sending ? 'Sending…' : 'Send'}</button>
      </form>
    </div>
  );
}
